import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const Analytics = ({ apiClient }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [customDateRange, setCustomDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [showCustomRange, setShowCustomRange] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period: selectedPeriod
      });
      
      if (showCustomRange && customDateRange.start_date && customDateRange.end_date) {
        params.append('start_date', customDateRange.start_date);
        params.append('end_date', customDateRange.end_date);
      }
      
      const response = await apiClient.get(`/restaurant/analytics?${params}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (e) => {
    setCustomDateRange({
      ...customDateRange,
      [e.target.name]: e.target.value
    });
  };

  const exportReport = async (format) => {
    try {
      const params = new URLSearchParams({
        period: selectedPeriod,
        format: format
      });
      
      if (showCustomRange && customDateRange.start_date && customDateRange.end_date) {
        params.append('start_date', customDateRange.start_date);
        params.append('end_date', customDateRange.end_date);
      }
      
      if (format === 'csv') {
        // For CSV download
        const response = await fetch(`${apiClient.defaults.baseURL}/restaurant/analytics/export?${params}`, {
          headers: {
            'Authorization': apiClient.defaults.headers.common['Authorization']
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `analytics_report_${selectedPeriod}.csv`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('Report exported successfully!');
        } else {
          throw new Error('Export failed');
        }
      } else {
        const response = await apiClient.get(`/restaurant/analytics/export?${params}`);
        
        // Download JSON
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_report_${selectedPeriod}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report exported successfully!');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatHour = (peak) => {
    // If peak has timestamp, convert to local timezone
    if (peak && peak.timestamp) {
      const date = new Date(peak.timestamp);
      const hour = date.getHours();
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:00 ${period}`;
    }
    // Legacy support for pre-formatted strings or direct hour values
    if (typeof peak === 'string') {
      return peak;
    }
    if (typeof peak === 'number') {
      const period = peak >= 12 ? 'PM' : 'AM';
      const displayHour = peak === 0 ? 12 : peak > 12 ? peak - 12 : peak;
      return `${displayHour}:00 ${period}`;
    }
    // If peak.hour is a number
    if (peak && typeof peak.hour === 'number') {
      const hour = peak.hour;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:00 ${period}`;
    }
    return peak?.hour || peak || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" style={{ width: '32px', height: '32px' }}></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="card">
        <div className="card-header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900">Analytics Dashboard</h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex space-x-2">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  data-testid="period-select"
                  className="form-input w-auto"
                >
                  <option value="daily">Today</option>
                  <option value="weekly">This Week</option>
                  <option value="monthly">This Month</option>
                </select>
                <button
                  onClick={() => setShowCustomRange(!showCustomRange)}
                  data-testid="custom-range-toggle"
                  className="btn-secondary text-sm"
                >
                  Custom Range
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => exportReport('json')}
                  data-testid="export-json-button"
                  className="btn-secondary text-sm"
                >
                  Export JSON
                </button>
                <button
                  onClick={() => exportReport('csv')}
                  data-testid="export-csv-button"
                  className="btn-secondary text-sm"
                >
                  Export CSV
                </button>
                <button
                  onClick={fetchAnalytics}
                  data-testid="refresh-analytics-button"
                  className="btn-primary text-sm"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Date Range */}
        {showCustomRange && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label htmlFor="start_date" className="form-label">
                  Start Date
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={customDateRange.start_date}
                  onChange={handleDateRangeChange}
                  data-testid="start-date-input"
                  className="form-input"
                />
              </div>
              <div>
                <label htmlFor="end_date" className="form-label">
                  End Date
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={customDateRange.end_date}
                  onChange={handleDateRangeChange}
                  data-testid="end-date-input"
                  className="form-input"
                />
              </div>
              <div>
                <button
                  onClick={fetchAnalytics}
                  data-testid="apply-date-range-button"
                  className="btn-primary w-full"
                >
                  Apply Range
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {analyticsData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Total Orders</h3>
              <p className="text-3xl font-bold text-blue-600" data-testid="total-orders">
                {analyticsData.summary.total_orders}
              </p>
            </div>
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Total Revenue</h3>
              <p className="text-3xl font-bold text-green-600" data-testid="total-revenue">
                {formatCurrency(analyticsData.summary.total_revenue)}
              </p>
            </div>
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Avg Order Value</h3>
              <p className="text-3xl font-bold text-orange-600" data-testid="avg-order-value">
                {formatCurrency(analyticsData.summary.avg_order_value)}
              </p>
            </div>
          </div>

          {/* Tax Breakdown Card */}
          {analyticsData.summary.tax_breakdown && Object.keys(analyticsData.summary.tax_breakdown).length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Tax Breakdown</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(analyticsData.summary.tax_breakdown).map(([component, amount]) => (
                  <div key={component} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-500 mb-1">{component}</p>
                    <p className={`text-2xl font-bold ${amount < 0 ? 'text-red-600' : 'text-purple-600'}`}>
                      {formatCurrency(amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Breakdown Chart */}
            {analyticsData.daily_breakdown && analyticsData.daily_breakdown.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold text-gray-900">Daily Breakdown</h3>
                </div>
                <div className="space-y-4">
                  {analyticsData.daily_breakdown.map((day, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-gray-900">{day.date}</p>
                        <p className="text-sm text-gray-500">{day.orders} orders</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(day.revenue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Peak Hours */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Peak Hours</h3>
              </div>
              <div className="space-y-3">
                {analyticsData?.peak_hours?.length > 0 ? 
                  analyticsData.peak_hours.slice(0, 5).map((peak, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-700">{formatHour(peak)}</span>
                      <div className="flex items-center space-x-2">
                        <div className="bg-blue-200 h-2 rounded-full" style={{ width: `${(peak.orders / (analyticsData.peak_hours?.[0]?.orders || 1)) * 100}px` }}></div>
                        <span className="text-sm font-medium text-gray-900">{peak.orders}</span>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center text-gray-500 py-4">
                      <p>No peak hour data available</p>
                    </div>
                  )
                }
              </div>
            </div>
          </div>

          {/* Popular Items */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Popular Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity Sold
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData.popular_items.map((item, index) => (
                    <tr key={index} data-testid={`popular-item-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(item.revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.orders}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {analyticsData.popular_items.length === 0 && (
                <div className="text-center py-8 text-gray-500" data-testid="no-popular-items">
                  No items data available for this period.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;